import { Checkbox, FormControl, FormControlLabel, Grid, InputLabel, TextField } from '@material-ui/core';
import { SaleType } from 'api/query/catalogue';
import Button from 'components/button';
import FormErrorMessage from 'components/form-error-message';
import { AlertIcon } from 'components/icons/alert';
import { DeleteOutlinedIcon } from 'components/icons/delete-outlined';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { getAllowedImageExtensions, getAllowedImageTypes, ImageUploadError, isValidImageType } from 'shared/image-upload-utils';
import { fileToBase64 } from 'utils/file-utils';
import { CatalogueFormField, catalogueFormValidation } from '..';
import { AddDetailsProps } from './add-details-props';
import './add-details.scss';

/**
 * Add additional catalogue details.
 *
 * Step 2 of the create/edit sales/semen catalogue.
 * @param props
 */
export const AddDetails: React.FC<AddDetailsProps> = (props) => {
  const {
    catalogue,
  } = props;

  const {
    register, errors, setValue, setError, clearError,
  } = useFormContext();

  const [imgSrc, setImgSrc] = useState<string>();

  /**
   * Gets the placeholder for the description input by the sale type.
   * @param saleType
   */
  const getDescriptionPlaceholder = (saleType: SaleType) => {
    switch (saleType) {
      case SaleType.PrivateTreatySale:
      case SaleType.SingleVendorAuction:
      default:
        return 'Brief description of sale';
      case SaleType.AllSemenCatalogue:
        return 'Brief description of your semen catalogue. You may want to include information such as AI centre locations';
    }
  };

  /**
   * Delete the logo
   */
  const deleteLogo = () => {
    setValue(CatalogueFormField.Logo, '');
    setImgSrc(undefined);
    clearError(CatalogueFormField.Logo);
    if (catalogue.catalogueImages && catalogue.catalogueImages.length) {
      catalogue.catalogueImages = [];
    }
  };

  /**
  * Gets the catalogue logo as a base 64 string to be displayed
  * in the img tag.
  *
  * Handle custom validation here because it's not supported for input type=file.
  */
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const logo = event.currentTarget.files && event.currentTarget.files[0];

    // If a logo exists and is a valid file type, set the image source to the
    // base64 value of the uploaded image.
    if (logo) {
      // Check if the file is one of the supported types and set the error if not.
      const isValidType = isValidImageType(logo.type, getAllowedImageTypes());
      if (!isValidType) {
        setError(CatalogueFormField.Logo, 'type', ImageUploadError.FileType);
      }

      // Check if the file is under the size limit and set the error if not.
      const isValidSize = logo.size < 5e+6; // 5MB
      if (!isValidSize) {
        setError(CatalogueFormField.Logo, 'size', ImageUploadError.FileSize);
      }

      // If the file is a valid type and size, get the base64 string
      // and set the img src.
      if (isValidType && isValidSize) {
        const getBase64 = async () => {
          const base64 = await fileToBase64(logo);
          setImgSrc(base64);
        };
        getBase64();
      }
    }
  };

  /**
   * Set the Image tag in edit catalogue
   */
  useEffect(() => {
    if (catalogue.catalogueImages && catalogue.catalogueImages.length) {
      setImgSrc(`data:image;base64,${catalogue.catalogueImages[0].image.imageBytes}`);
    }
  }, [catalogue]);

  return (
    <Grid className="sg-add-details form-container" item xs={12} sm={10} md={6}>
      <div className="form-section">
        <Grid container spacing={3}>
          {/** RamSelect (only for Sale catalogues) */}
          {(catalogue.saleType === SaleType.PrivateTreatySale
            || catalogue.saleType === SaleType.SingleVendorAuction) && (
              <Grid item xs={12}>
                <FormControlLabel
                  label="Add to RamSelect.com (free of charge)"
                  control={(
                    <Checkbox
                      inputProps={{
                        name: CatalogueFormField.RamSelect,
                      }}
                      defaultChecked={!!catalogue[CatalogueFormField.RamSelect]}
                      inputRef={register}
                    />
                  )}
                />
              </Grid>
            )}
          {/** Website */}
          <Grid item xs={12}>
            <TextField
              label="Website (optional)"
              placeholder="Enter URL"
              defaultValue={catalogue.contactUrl}
              name={CatalogueFormField.Website}
              error={!!errors[CatalogueFormField.Website]}
              helperText={<FormErrorMessage errors={errors} name={CatalogueFormField.Website} />}
              inputRef={register}
            />
            <div className="form__url-format">
              The URL format should be <b>https://www.myFlock.com.au</b>.
            </div>
          </Grid>
          {/** Auction website (only for Single Vendor Auction sale catalogues) */}
          {catalogue.saleType === SaleType.SingleVendorAuction && (
            <Grid item xs={12}>
              <TextField
                label="Auction website (optional)"
                placeholder="Enter URL"
                defaultValue={catalogue.auctionUrl}
                name={CatalogueFormField.AuctionWebsite}
                error={!!errors[CatalogueFormField.AuctionWebsite]}
                helperText={<FormErrorMessage errors={errors} name={CatalogueFormField.AuctionWebsite} />}
                inputRef={register}
              />
            </Grid>
          )}
          {/** Sale description */}
          <Grid item xs={12}>
            <TextField
              label="Sale description (optional)"
              placeholder={getDescriptionPlaceholder(catalogue.saleType)}
              multiline
              rows={10}
              defaultValue={catalogue.description}
              name={CatalogueFormField.SaleDescription}
              error={!!errors[CatalogueFormField.SaleDescription]}
              helperText={<FormErrorMessage errors={errors} name={CatalogueFormField.SaleDescription} />}
              inputRef={register(catalogueFormValidation[CatalogueFormField.SaleDescription])}
            />
          </Grid>
          {/** Logo */}
          <Grid className="form-add-logo" item xs={12}>
            <FormControl>
              <InputLabel
                shrink
              >
                Add logo (optional)
              </InputLabel>
              <div className="logo">
                <div className="logo__file-upload">
                  <label htmlFor="logo-button">
                    <input
                      id="logo-button"
                      accept={getAllowedImageExtensions().toString()}
                      name={CatalogueFormField.Logo}
                      type="file"
                      ref={register}
                      onChange={handleFileChange}
                    />
                    {!imgSrc && (
                      <>
                        <Button
                          className="logo__button"
                          variant="outlined"
                          component="div"
                        >
                          Browse files
                        </Button>
                        <div className="logo__supported-files">
                          <div><b>Supported files:</b> PNG, JPG OR JPEG <br/>
                            <b>Recommended logo dimension:</b> <br/>
                            Max 500 x 500 pixel and a max file size of 500kb.
                            This will ensure that catalogues load quickly.
                          </div>
                        </div>
                      </>
                    )}
                  </label>
                </div>
                {imgSrc && (
                  <img
                    className="logo__img"
                    src={imgSrc}
                    alt="Logo"
                    typeof="string"
                  />
                )}
              </div>
              {imgSrc && (
                <div className="logo__delete">
                  <Button
                    variant="outlined"
                    startIcon={<DeleteOutlinedIcon />}
                    onClick={deleteLogo}
                  >
                    Delete Logo
                  </Button>
                </div>
              )}
              {errors[CatalogueFormField.Logo] && (
                <div className="logo__error">
                  <AlertIcon />{errors[CatalogueFormField.Logo].message}
                </div>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </div>
    </Grid>
  );
};
